import { Trans, useLingui } from "@lingui/react/macro";
import { useId, useState } from "react";

import { Button } from "@/shared/ui/button";
import { ToggleGroup, ToggleGroupItem } from "@/shared/ui/toggle-group";

import { EXTRA_FILTERS } from "../../lib/EXTRA_FILTERS";
import { hasFilters } from "../../lib/hasFilters";
import type { HeroStat } from "../../lib/heroStats";
import type { MatchFilters as Filters } from "../../lib/matchFiltersSchema";
import { NO_FILTERS } from "../../lib/NO_FILTERS";
import { ExtraFilters } from "./ExtraFilters";
import { HeroFilter } from "./HeroFilter";
import { PeriodFilter } from "./PeriodFilter";
import { SelectedHeroes } from "./SelectedHeroes";

type MatchFiltersProps = {
  filters: Filters;
  onChange: (filters: Filters) => void;
  // Герои и дни загруженных матчей: варианты в выборе героев и точки в календаре.
  played: readonly HeroStat[];
  matchDays: readonly Date[];
  now: Date;
};

// Фильтры над списком матчей по макету: тип матча, результат, герои, период, «Ещё фильтры»,
// «Сбросить всё».
export const MatchFilters = ({ filters, onChange, played, matchDays, now }: MatchFiltersProps) => {
  const { t } = useLingui();
  const extraId = useId();
  const extraCount = EXTRA_FILTERS.filter((key) => filters[key] !== undefined).length;
  // По ссылке с включёнными дополнительными фильтрами панель сразу открыта.
  const [isExtraOpen, setIsExtraOpen] = useState(extraCount > 0);
  const heroes = filters.heroes ?? [];

  const patch = (changes: Partial<Filters>) => {
    onChange({ ...filters, ...changes });
  };
  const setHeroes = (next: number[]) => {
    patch({ heroes: next.length === 0 ? undefined : next });
  };

  return (
    <div className="flex flex-col gap-2.5">
      <div className="flex flex-wrap items-center gap-x-3.5 gap-y-2.5">
        <ToggleGroup
          type="single"
          aria-label={t`Match type`}
          value={filters.mode ?? "all"}
          onValueChange={(mode) => {
            if (mode === "all") {
              patch({ mode: undefined });
            } else if (mode === "ranked" || mode === "unranked") {
              patch({ mode });
            }
          }}
        >
          <ToggleGroupItem value="all">
            <Trans>All</Trans>
          </ToggleGroupItem>
          <ToggleGroupItem value="ranked">
            <Trans>Ranked</Trans>
          </ToggleGroupItem>
          <ToggleGroupItem value="unranked">
            <Trans>Unranked</Trans>
          </ToggleGroupItem>
        </ToggleGroup>
        <ToggleGroup
          type="single"
          aria-label={t`Result`}
          value={filters.result ?? "all"}
          onValueChange={(result) => {
            if (result === "all") {
              patch({ result: undefined });
            } else if (result === "win" || result === "loss") {
              patch({ result });
            }
          }}
        >
          <ToggleGroupItem value="all">
            <Trans>All</Trans>
          </ToggleGroupItem>
          <ToggleGroupItem value="win">
            <Trans>Wins</Trans>
          </ToggleGroupItem>
          <ToggleGroupItem value="loss">
            <Trans>Losses</Trans>
          </ToggleGroupItem>
        </ToggleGroup>
        <HeroFilter selected={heroes} played={played} onChange={setHeroes} />
        <PeriodFilter value={filters} matchDays={matchDays} now={now} onChange={patch} />
        <Button
          variant="outline"
          aria-expanded={isExtraOpen}
          aria-controls={isExtraOpen ? extraId : undefined}
          className="h-[38px] rounded-lg border-border-strong bg-transparent px-3 text-[13px] font-normal text-accent hover:bg-surface-raised"
          onClick={() => {
            setIsExtraOpen(!isExtraOpen);
          }}
        >
          {isExtraOpen ? <Trans>Hide filters</Trans> : <Trans>More filters</Trans>}
          {extraCount > 0 && (
            <>
              {" "}
              <span>· {extraCount}</span>
            </>
          )}
        </Button>
        {hasFilters(filters) && (
          <Button
            variant="link"
            size="sm"
            className="px-1.5 text-muted underline"
            onClick={() => {
              onChange(NO_FILTERS);
            }}
          >
            <Trans>Reset all</Trans>
          </Button>
        )}
      </div>
      {heroes.length > 0 && <SelectedHeroes selected={heroes} onChange={setHeroes} />}
      {isExtraOpen && <ExtraFilters id={extraId} filters={filters} onChange={patch} />}
    </div>
  );
};
